import { QuestionInterface, QuestionnaireInterface } from '@api/assessmentQuestionnaireApiHandler';
import { DEFAULT_V2_IMAGE_PATH } from '@config/questionnaire-v2-images.config';
import { KeyConfigurations } from '@enums/KeyConfigurations';
import { colorMap } from '@helpers/color.helper';
import { smoothScrollTo } from '@helpers/general.helpers';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { MotionImage, MotionText } from '@shared/atoms';
import { ShareThoughtDrawer, SystemButton } from '@shared/molecules';
import React, { useMemo, useState } from 'react';
import { answerLikertMultipleQuestion } from 'store/Slices/QuestionnaireSliceV2';
import { QuestionnaireBrakeOff } from '../QuestionnaireBrakeOff/QuestionnaireBrakeOff';
import { ICON_BG_FALL_BACK_COLOR } from '../SingleQuestionnaire/config';
import styles from './index.module.scss';

export const ContentWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <main className={styles.wrapperContainer} role="main" aria-label="Assessment content">
    <div className={styles.wrapperCard}>{children}</div>
  </main>
);

interface LikertMultipleQuestionsProps {
  questions: QuestionInterface[];
  questionsCount?: number;
  answerDisplayingType?: 'row' | 'column';
  shareThoughtEnabled?: boolean;
  onComplete?: () => void;
  questionnaire: QuestionnaireInterface | null;
}

export const LikertMultipleQuestions: React.FC<LikertMultipleQuestionsProps> = ({
  questions,
  questionsCount = 4,
  answerDisplayingType = 'row',
  shareThoughtEnabled,
  onComplete,
  questionnaire,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [openDrawerQuestionId, setOpenDrawerQuestionId] = useState<number | null>(null);
  const [showBreakOff, setShowBreakOff] = useState(false);

  const dispatch = useAppDispatch();
  const { answers } = useAppSelector((state) => state.questionnaireV2 || {});

  // Filter questions and identify break-off questions
  const { filteredQuestions, lastBreakOffQuestion } = useMemo(() => {
    // Remove first question if it's a break-off
    const questionsWithoutFirstBreakOff = questions[0]?.isBrakeOff ? questions.slice(1) : questions;

    // Find last break-off question
    const lastBreakOff = questionsWithoutFirstBreakOff[questionsWithoutFirstBreakOff.length - 1]
      ?.isBrakeOff
      ? questionsWithoutFirstBreakOff[questionsWithoutFirstBreakOff.length - 1]
      : null;

    // Remove last break-off from regular questions
    const filtered = lastBreakOff
      ? questionsWithoutFirstBreakOff.slice(0, -1)
      : questionsWithoutFirstBreakOff;

    return {
      filteredQuestions: filtered,
      lastBreakOffQuestion: lastBreakOff,
    };
  }, [questions]);

  const questionsWithBrakeOffs = filteredQuestions;
  const totalPages = Math.ceil(questionsWithBrakeOffs.length / questionsCount);

  const currentQuestions = questionsWithBrakeOffs.slice(
    currentPage * questionsCount,
    (currentPage + 1) * questionsCount,
  );

  const totalAnsweredQuestions = questionsWithBrakeOffs.filter(
    (question) => answers[question.id],
  ).length;
  const completionPercentage = Math.round(
    (totalAnsweredQuestions / questionsWithBrakeOffs.length) * 100,
  );
  const currentQuestionNumber = Math.min(totalAnsweredQuestions, questionsWithBrakeOffs.length);

  const handleOptionSelect = (questionId: number, optionId: number, answerScore: number) => {
    dispatch(
      answerLikertMultipleQuestion({
        questionId,
        answerId: Number(optionId),
        answerScore: Number(answerScore),
      }),
    );
  };

  // Function to handle drawer open/close for specific questions
  const handleDrawerOpen = (questionId: number) => {
    setOpenDrawerQuestionId(questionId);
  };

  const handleDrawerClose = () => {
    setOpenDrawerQuestionId(null);
  };

  const goToNextPage = () => {
    smoothScrollTo(0, 0);
    // Close any open drawer when navigating
    setOpenDrawerQuestionId(null);

    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else if (lastBreakOffQuestion) {
      // If there's a last break-off question, show it
      setShowBreakOff(true);
    } else if (onComplete) {
      onComplete();
    }
  };

  const goToPrevPage = () => {
    smoothScrollTo(0, 0);
    // Close any open drawer when navigating
    setOpenDrawerQuestionId(null);

    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleBreakOffNext = () => {
    if (onComplete) {
      onComplete();
    }
  };

  const handleBreakOffPrevious = () => {
    setShowBreakOff(false);
    // Go back to the last page of questions
    setCurrentPage(totalPages - 1);
  };

  const isCurrentPageCompleted = () => currentQuestions.every((question) => answers[question.id]);

  const isPageCompleted = (pageIndex: number) => {
    const pageQuestions = questionsWithBrakeOffs.slice(
      pageIndex * questionsCount,
      (pageIndex + 1) * questionsCount,
    );

    return pageQuestions.every((question) => answers[question.id]);
  };

  // If showing break-off, render the break-off component
  if (showBreakOff && lastBreakOffQuestion) {
    return (
      <ContentWrapper>
        <QuestionnaireBrakeOff
          icon={
            lastBreakOffQuestion?.icon
              ? `${DEFAULT_V2_IMAGE_PATH}/${lastBreakOffQuestion?.icon}`
              : null
          }
          title={lastBreakOffQuestion?.questionText}
          htmlDescription={lastBreakOffQuestion?.description}
          nextButtonName={lastBreakOffQuestion?.buttonText || 'Continue'}
          onNext={handleBreakOffNext}
          prevButtonEnabled
          onPrevious={handleBreakOffPrevious}
          iconBackgroundColor={questionnaire?.color ? questionnaire?.color : null}
        />
      </ContentWrapper>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <MotionText as="h2" text={`${(questionnaire && questionnaire?.name) || ''} Assessment`} />
        <MotionText
          as="text"
          className={styles.shortDescription}
          text={(questionnaire && questionnaire?.shortDescription) || ''}
        />

        {/* Progress Information */}
        <div className={styles.progressInfo}>
          <div className={styles.questionProgress}>
            Question {currentQuestionNumber} of {questionsWithBrakeOffs.length}
          </div>
          <div className={styles.completionPercentage}>{completionPercentage}% completed</div>
        </div>

        {/* Progress Bar */}
        <div className={styles.progressBarContainer}>
          <div className={styles.progressBar} style={{ width: `${completionPercentage}%` }} />
        </div>
      </div>

      <div className={styles.questionsContainer}>
        {currentQuestions.map((question, index) => (
          <div key={question.id} className={styles.questionCard}>
            <div className={styles.cardContent}>
              {question?.icon && (
                <div
                  className={styles.indexNumber}
                  style={{
                    background: questionnaire?.color
                      ? colorMap[questionnaire?.color]?.mediumColor
                      : ICON_BG_FALL_BACK_COLOR,
                  }}
                >
                  <MotionImage
                    src={`${DEFAULT_V2_IMAGE_PATH}/${question?.icon}`}
                    alt=""
                    aria-hidden="true"
                  />
                </div>
              )}
              <div className={styles.contentWrapper}>
                <div className={styles.questionContainer}>
                  <h3 className={styles.questionText}>{question?.questionText || ''}</h3>
                  {shareThoughtEnabled && (
                    <MotionText
                      as="text"
                      text="Share thoughts"
                      className={styles.shareThoughtText}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDrawerOpen(question.id); //  Pass specific question ID
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === KeyConfigurations.ENTER ||
                          e.key === KeyConfigurations.SPACE
                        ) {
                          e.preventDefault();
                          handleDrawerOpen(question.id); // Pass specific question ID
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-haspopup="dialog"
                      aria-expanded={openDrawerQuestionId === question.id}
                    />
                  )}
                </div>

                {shareThoughtEnabled && (
                  <ShareThoughtDrawer
                    open={openDrawerQuestionId === question.id}
                    handleClose={handleDrawerClose}
                    handleOpen={() => handleDrawerOpen(question.id)}
                    questionId={question.id}
                    questionLabel={question?.questionText || ''}
                  />
                )}

                <div
                  className={`${styles.optionsContainer} ${
                    answerDisplayingType === 'row' ? styles.rowDisplay : styles.columnDisplay
                  }`}
                >
                  {question &&
                    question?.questionnairesV2QuestionResponseScales &&
                    question.questionnairesV2QuestionResponseScales?.questionnairesV2QuestionResponseScaleOptions?.map(
                      (option) => (
                        <button
                          key={option.id}
                          className={`${styles.optionButton} ${
                            answers[question.id]?.answerId === option.id ? styles.selected : ''
                          }`}
                          onClick={() => handleOptionSelect(question.id, option.id, option.score)}
                        >
                          {option.optionText}
                        </button>
                      ),
                    )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.navigationContainer}>
        <SystemButton
          onClick={goToPrevPage}
          disabled={currentPage === 0}
          variant="outlinedPrimarySkyBlue"
        >
          <NavigateBeforeIcon aria-hidden="true" />
          Previous &nbsp;
        </SystemButton>

        <div className={styles.pageIndicator}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <span
              key={index}
              className={`${styles.pageIndicatorDot} ${
                index === currentPage || isPageCompleted(index) ? styles.active : ''
              }`}
            />
          ))}
        </div>

        <SystemButton onClick={goToNextPage} disabled={!isCurrentPageCompleted()}>
          {currentPage !== totalPages - 1 && <>&nbsp;&nbsp;&nbsp;</>}
          {/* Show "Next" instead of "Finish" when there's a last break-off question */}
          {currentPage === totalPages - 1 ? (lastBreakOffQuestion ? 'Next' : 'Finish') : 'Next'}
          {currentPage === totalPages - 1 ? (
            lastBreakOffQuestion ? (
              <NavigateNextIcon aria-hidden="true" />
            ) : (
              ''
            )
          ) : (
            <NavigateNextIcon aria-hidden="true" />
          )}
        </SystemButton>
      </div>
    </div>
  );
};

LikertMultipleQuestions.defaultProps = {
  questionsCount: 4,
  answerDisplayingType: 'row',
  shareThoughtEnabled: false,
  onComplete: () => null,
};

//
